import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import Layout from "../../../universal/layout/Layout";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import SavDetailModal from "./SavDetailModal";
import ClientFicheModal from "app/orderTool/components/ClientFicheModal";
import HelpTip from "app/orderTool/components/HelpTip";
import DocumentsButton from "app/orderTool/components/DocumentsButton";
import VisiteGuidee from "app/orderTool/components/VisiteGuidee";
import { ColumnEmpty } from "app/orderTool/components/pageUi";
import { getSavs, getSavConstants, patchSavStatut } from "state/sav/actions";
import { filterSavsByCdc } from "../pipelineB2b/pipelineFilters";
import {
  MOTIF_LABELS,
  STATUT_LABELS,
  CANAL_LABELS,
  labelFor,
} from "./savLabels";


// Recherche sans accents, casse, ponctuation ni espaces
const normaliser = (texte) =>
  (texte || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    // Ni ponctuation ni espaces : « diplom » ou « diplo'm » trouvent « Diplo'Mates »
    .replace(/[^a-z0-9]/g, "");

const CHARGES = ["Julie", "Valentin", "Camille"];

const COLONNES = [
  {
    id: "A_TRAITER",
    titre: "À traiter",
    couleur: "#d9534f",
    aide: "Le SAV vient d'être déclaré (par un compagnon, le client ou en interne) et n'a pas encore été pris en charge. Ouvrez la carte pour commencer le traitement.",
  },
  {
    id: "EN_COURS",
    titre: "En cours",
    couleur: "#f0ad4e",
    aide: "Le SAV est pris en charge. Tant qu'il n'est pas réglé, la facturation de la commande liée est bloquée (pas de passage automatique en facture).",
  },
  {
    id: "REGLE",
    titre: "Réglé",
    couleur: "#429853",
    aide: "Le SAV est clos : solution apportée et éventuel geste commercial accordé. La facturation de la commande n'est plus bloquée par ce SAV.",
  },
];

const STATUT_BY_COLUMN = {
  A_TRAITER: "A_TRAITER",
  EN_COURS: "EN_COURS",
  REGLE: "REGLE",
};

/**
 * Visite guidée du board SAV. Remplace l'ancien HelpTip « comment ça marche ? »
 * de l'en-tête : même contenu, mais montré sur l'écran plutôt que raconté.
 */
const ETAPES_VISITE = [
  {
    cible: "board",
    titre: "Le SAV en un coup d'œil",
    texte:
      "Chaque carte est un incident sur une prestation B2B. Les trois colonnes suivent son traitement, de gauche à droite : à traiter, en cours, réglé.",
    placement: "bas",
  },
  {
    cible: "declarer-sav",
    titre: "Déclarer un incident",
    texte:
      "C'est ici qu'on ouvre un SAV : motif, service concerné, commande et description. À savoir : les livreurs peuvent aussi en déclarer un depuis le Companion, à la fin d'une livraison — ces SAV arrivent directement dans « À traiter ».",
  },
  {
    cible: "colonne-A_TRAITER",
    titre: "Prendre un dossier",
    texte:
      "Cliquez sur une carte pour la traiter : étapes, geste commercial, brouillon de carte d'excuse, fil de commentaires. Cocher « Prise en charge » fait passer le dossier en cours.",
  },
  {
    cible: "colonne-REGLE",
    titre: "Clôturer, et débloquer la facture",
    texte:
      "Tant qu'un SAV n'est pas réglé, la facturation de sa commande est bloquée. La clôture exige d'avoir coché les étapes : on ne ferme pas un dossier dont on n'a rien proposé au client.",
  },
  {
    cible: "filtre-a-rattacher",
    titre: "Les SAV sans commande",
    texte:
      "Certains SAV, surtout ceux venus de l'ancien suivi, n'ont pas de commande identifiée. Ce filtre les isole pour les raccrocher à la main depuis la carte.",
  },
  {
    cible: "filtres-type-date",
    titre: "Filtrer par type et par date",
    texte:
      "Le type correspond au motif du SAV (un dossier à plusieurs motifs apparaît pour chacun d'eux). Les dates portent sur la date de l'incident : un SAV sans date d'incident est masqué dès qu'une date est saisie.",
  },
  {
    cible: "filtre-cdc",
    titre: "Ne voir que vos dossiers",
    texte:
      "Ce filtre restreint le board à un chargé de clientèle. Une notice complète reste disponible à tout moment sur /notice/sav-b2b.",
  },
];

/** Date d'incident au format AAAA-MM-JJ, comparable aux <input type="date"> */
const jourIncident = (sav) => (sav.date_incident ? String(sav.date_incident).slice(0, 10) : null);

const PipelineSav = () => {
  // Titre de l'onglet navigateur (sinon il reste celui de la page précédente).
  useEffect(() => {
    const prev = document.title;
    document.title = "SAV B2B — Le Moulin";
    return () => { document.title = prev; };
  }, []);

  const dispatch = useDispatch();
  const history = useHistory();
  const savs = useSelector((state) => state.sav.savs);
  const isLoading = useSelector((state) => state.sav.isLoading);
  const isInitialized = useSelector((state) => state.sav.isInitialized);
  const constants = useSelector((state) => state.sav.constants);
  const [chargeFiltre, setChargeFiltre] = useState("");
  const [filtreARattacher, setFiltreARattacher] = useState(false);
  const [motifFiltre, setMotifFiltre] = useState("");
  const [dateDu, setDateDu] = useState("");
  const [dateAu, setDateAu] = useState("");
  // Recherche texte (retour terrain Keep 26/10) : référence, client, entreprise, commande, description
  const [recherche, setRecherche] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSavId, setModalSavId] = useState(null);
  const [modalSavCard, setModalSavCard] = useState(null);
  const [openingSavId, setOpeningSavId] = useState(null);
  const [boardBusy, setBoardBusy] = useState(false);
  const [fiche, setFiche] = useState(null); // { clientId, entrepriseId } | null

  const [visiteOuverte, setVisiteOuverte] = useState(false);

  const reload = useCallback(() => {
    dispatch(getSavs());
  }, [dispatch]);

  useEffect(() => {
    reload();
    if (!constants) {
      dispatch(getSavConstants());
    }
  }, [reload, dispatch, constants]);

  // Ouverture directe d'une carte via ?sav_id= : c'est le lien posé dans l'alerte
  // Connecteam à la déclaration d'un SAV, pour tomber sur le bon dossier sans
  // avoir à le chercher dans le board. Joué une seule fois par chargement.
  const deepLinkTraite = useRef(false);
  useEffect(() => {
    if (deepLinkTraite.current || !isInitialized) {
      return;
    }
    const savId = new URLSearchParams(window.location.search).get("sav_id");
    if (!savId) {
      return;
    }
    deepLinkTraite.current = true;
    const cible = (savs || []).find((s) => String(s.id) === String(savId));
    setModalSavId(parseInt(savId, 10));
    setModalSavCard(cible || null);
    setModalOpen(true);
  }, [isInitialized, savs]);

  const filteredSavs = useMemo(() => {
    let rows = filterSavsByCdc(savs, chargeFiltre);
    if (filtreARattacher) {
      rows = rows.filter((s) => s.a_rattacher);
    }
    if (motifFiltre) {
      rows = rows.filter((s) => s.motif === motifFiltre || (s.motifs || []).includes(motifFiltre));
    }
    if (dateDu || dateAu) {
      rows = rows.filter((s) => {
        const jour = jourIncident(s);
        return jour && (!dateDu || jour >= dateDu) && (!dateAu || jour <= dateAu);
      });
    }
    const q = normaliser(recherche.trim());
    if (q) {
      rows = rows.filter((s) =>
        [s.reference, s.client_nom, s.entreprise_nom, s.commande_groupee_numero, s.prestation_label, s.description]
          .some((champ) => normaliser(champ).includes(q))
      );
    }
    return rows;
  }, [savs, chargeFiltre, filtreARattacher, motifFiltre, dateDu, dateAu, recherche]);

  const filtresTypeDateActifs = !!(motifFiltre || dateDu || dateAu);

  const openSav = (sav) => {
    setModalSavId(sav.id);
    setModalSavCard(sav);
    setModalOpen(true);
    setOpeningSavId(sav.id);
    setTimeout(() => setOpeningSavId(null), 400);
  };

  const openCreate = () => {
    setModalSavId(null);
    setModalSavCard(null);
    setModalOpen(true);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.droppableId === result.source.droppableId) {
      return;
    }
    const to = result.destination.droppableId;
    const id = parseInt(result.draggableId.replace("sav-", ""), 10);
    const targetStatut = STATUT_BY_COLUMN[to];
    if (!targetStatut) {
      return;
    }

    const sav = filteredSavs.find((s) => s.id === id) || savs.find((s) => s.id === id);
    const allowed = constants?.transitions?.[sav?.statut] || [];
    if (!allowed.includes(targetStatut)) {
      toast.info(`Transition ${labelFor(STATUT_LABELS, sav?.statut)} → ${labelFor(STATUT_LABELS, targetStatut)} non autorisée`);
      return;
    }

    if (targetStatut === "REGLE" && sav && (!sav.date_prise_en_charge || !sav.date_solution_proposee)) {
      toast.warning("Complétez les étapes dans la fiche SAV avant la clôture");
      openSav(sav);
      return;
    }

    setBoardBusy(true);
    const resp = await dispatch(patchSavStatut(id, targetStatut));
    setBoardBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Changement de statut refusé");
    } else {
      toast.success("Statut mis à jour");
    }
    reload();
  };

  const cardsByColumn = COLONNES.reduce((acc, col) => {
    acc[col.id] = filteredSavs.filter((s) => s.statut === col.id);
    return acc;
  }, {});

  const boardLoading = isLoading && !isInitialized;

  const renderSavCard = (sav, index) => {
    const isOpening = openingSavId === sav.id;
    return (
      <Draggable key={`sav-${sav.id}`} draggableId={`sav-${sav.id}`} index={index}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <Card
              className="mb-2"
              style={{
                cursor: isOpening ? "wait" : "pointer",
                opacity: isOpening ? 0.65 : 1,
                pointerEvents: isOpening ? "none" : "auto",
              }}
              onClick={() => openSav(sav)}
            >
              <Card.Body className="p-2">
                <div className="d-flex justify-content-between align-items-center">
                  <b>{sav.reference}</b>
                  {isOpening ? (
                    <Spinner animation="border" size="sm" variant="success" />
                  ) : sav.charge_de_clientele ? (
                    <Badge variant="secondary">{sav.charge_de_clientele}</Badge>
                  ) : null}
                </div>
                <small>
                  {labelFor(MOTIF_LABELS, sav.motif)}
                  <br />
                  {sav.prestation_label || sav.commande_groupee_numero || "Sans rattachement"}
                  {sav.client_nom ? " — " : ""}
                  {sav.client_nom && (sav.client_id || sav.entreprise_id) ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setFiche({ clientId: sav.client_id, entrepriseId: sav.entreprise_id });
                      }}
                    >
                      {sav.client_nom}
                    </a>
                  ) : (
                    sav.client_nom || ""
                  )}
                  {sav.entreprise_nom ? (
                    (sav.client_id || sav.entreprise_id) ? (
                      <>
                        {" ("}
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFiche({ clientId: sav.client_id, entrepriseId: sav.entreprise_id });
                          }}
                        >
                          {sav.entreprise_nom}
                        </a>
                        {")"}
                      </>
                    ) : ` (${sav.entreprise_nom})`
                  ) : ""}
                </small>
                <div className="mt-1">
                  <Badge variant="light" className="mr-1">{labelFor(CANAL_LABELS, sav.canal_declaration)}</Badge>
                  {sav.a_rattacher && <Badge variant="warning">À rattacher</Badge>}
                </div>
                {sav.commande_groupee_id ? (
                  <div className="mt-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="py-0 px-1"
                      style={{ fontSize: "0.75rem" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        history.push(`/commande?order_id=${sav.commande_groupee_id}&order_type=v2&from=sav`);
                      }}
                    >
                      Voir le devis
                    </Button>
                  </div>
                ) : null}
                {sav.documents && sav.documents.length > 0 && (
                  <div className="mt-1">
                    <DocumentsButton documents={sav.documents} stopPropagation />
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <Layout>
      <div style={{ padding: "15px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">
            Pipeline SAV B2B
            {isLoading && isInitialized && (
              <Spinner animation="border" size="sm" variant="success" className="ml-2" />
            )}
          </h4>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-success"
              size="sm"
              className="mr-3"
              onClick={() => setVisiteOuverte(true)}
              title="Revoir la visite guidée de l'écran"
            >
              Comment ça marche&nbsp;?
            </Button>
            <Form.Check
              type="checkbox"
              id="filtre-a-rattacher"
              label="À rattacher"
              className="mr-3 mb-1"
              data-visite="filtre-a-rattacher"
              checked={filtreARattacher}
              onChange={(e) => setFiltreARattacher(e.target.checked)}
            />
            <Form.Control
              as="select"
              size="sm"
              style={{ width: "220px" }}
              className="mr-2"
              data-visite="filtre-cdc"
              value={chargeFiltre}
              onChange={(e) => setChargeFiltre(e.target.value)}
              disabled={boardBusy}
            >
              <option value="">Tous les chargés de clientèle</option>
              {CHARGES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Control>
            <Button
              variant="success"
              size="sm"
              onClick={openCreate}
              disabled={boardBusy}
              data-visite="declarer-sav"
            >
              + Déclarer un SAV
            </Button>
          </div>
        </div>

        <div className="d-flex align-items-center flex-wrap mb-3" data-visite="filtres-type-date">
          <Form.Control
            type="search"
            size="sm"
            style={{ width: "280px" }}
            className="mr-3"
            placeholder="Rechercher : n° SAV, client, entreprise, commande…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
          />
          <Form.Control
            as="select"
            size="sm"
            style={{ width: "240px" }}
            className="mr-3"
            value={motifFiltre}
            onChange={(e) => setMotifFiltre(e.target.value)}
          >
            <option value="">Tous les types de SAV</option>
            {Object.entries(MOTIF_LABELS).map(([code, libelle]) => (
              <option key={code} value={code}>{libelle}</option>
            ))}
          </Form.Control>
          <small className="text-muted mr-2">Incident du</small>
          <Form.Control
            type="date"
            size="sm"
            style={{ width: "150px" }}
            className="mr-2"
            value={dateDu}
            max={dateAu || undefined}
            onChange={(e) => setDateDu(e.target.value)}
          />
          <small className="text-muted mr-2">au</small>
          <Form.Control
            type="date"
            size="sm"
            style={{ width: "150px" }}
            className="mr-3"
            value={dateAu}
            min={dateDu || undefined}
            onChange={(e) => setDateAu(e.target.value)}
          />
          {filtresTypeDateActifs && (
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => { setMotifFiltre(""); setDateDu(""); setDateAu(""); }}
            >
              Effacer ces filtres
            </Button>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <LoadingOverlay
            show={boardLoading || boardBusy}
            message={boardBusy ? "Mise à jour du statut…" : "Chargement des SAV…"}
            minHeight={320}
          />
          <DragDropContext onDragEnd={handleDragEnd}>
            <div
              style={{ display: "flex", gap: "12px", alignItems: "flex-start", overflowX: "auto" }}
              data-visite="board"
            >
              {COLONNES.map((colonne) => (
                <div key={colonne.id} style={{ flex: "1 0 30%", minWidth: "280px" }} data-visite={`colonne-${colonne.id}`}>
                  <div style={{
                    background: colonne.couleur, color: "#fff", padding: "8px 12px",
                    borderRadius: "6px 6px 0 0", fontWeight: "bold",
                  }}>
                    {colonne.titre}{" "}
                    <Badge variant="light">{(cardsByColumn[colonne.id] || []).length}</Badge>
                    {colonne.aide && (
                      <span style={{ color: "#fff" }}>
                        <HelpTip title={colonne.titre}>{colonne.aide}</HelpTip>
                      </span>
                    )}
                  </div>
                  <Droppable droppableId={colonne.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          background: snapshot.isDraggingOver ? "#dff0d8" : "#f5f5f5",
                          padding: "8px", minHeight: "300px", borderRadius: "0 0 6px 6px",
                        }}
                      >
                        {(cardsByColumn[colonne.id] || []).map((sav, index) => renderSavCard(sav, index))}
                        {!snapshot.isDraggingOver && (cardsByColumn[colonne.id] || []).length === 0 && (
                          <ColumnEmpty>Aucun SAV</ColumnEmpty>
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        </div>

        <VisiteGuidee etapes={ETAPES_VISITE} show={visiteOuverte} onClose={() => setVisiteOuverte(false)} />

        <SavDetailModal
          show={modalOpen}
          savId={modalSavId}
          savCard={modalSavCard}
          constants={constants}
          onClose={() => { setModalOpen(false); setModalSavCard(null); }}
          onSaved={reload}
        />

        <ClientFicheModal
          show={!!fiche}
          clientId={fiche?.clientId}
          entrepriseId={fiche?.entrepriseId}
          onClose={() => setFiche(null)}
        />
      </div>
    </Layout>
  );
};

export default PipelineSav;
